import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type Theme = 'light' | 'dark';

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    private readonly THEME_KEY = 'app-theme';
    private currentThemeSubject: BehaviorSubject<Theme>;
    public currentTheme$: Observable<Theme>;

    constructor() {
        // Load theme from localStorage or default to light
        const savedTheme = this.getSavedTheme();
        this.currentThemeSubject = new BehaviorSubject<Theme>(savedTheme);
        this.currentTheme$ = this.currentThemeSubject.asObservable();

        // Apply theme on init
        this.applyTheme(savedTheme);
    }

    /**
     * Get current theme value
     */
    get currentTheme(): Theme {
        return this.currentThemeSubject.value;
    }

    /**
     * Toggle between light and dark theme
     */
    toggleTheme(): void {
        const newTheme: Theme = this.currentTheme === 'light' ? 'dark' : 'light';
        this.setTheme(newTheme);
    }

    /**
     * Set specific theme
     */
    setTheme(theme: Theme): void {
        this.currentThemeSubject.next(theme);
        this.applyTheme(theme);
        this.saveTheme(theme);
    }

    /**
     * Check if current theme is dark
     */
    isDarkTheme(): boolean {
        return this.currentTheme === 'dark';
    }

    /**
     * Apply theme to document
     */
    private applyTheme(theme: Theme): void {
        const root = document.documentElement;
        const body = document.body;

        // Apply to html element
        root.setAttribute('data-theme', theme);
        root.classList.remove('theme-light', 'theme-dark');
        root.classList.add(`theme-${theme}`);

        // Apply to body element
        body.setAttribute('data-theme', theme);
        body.classList.remove('theme-light', 'theme-dark');
        body.classList.add(`theme-${theme}`);

        console.log(`Theme applied: ${theme}`);
    }

    /**
     * Save theme to localStorage
     */
    private saveTheme(theme: Theme): void {
        try {
            localStorage.setItem(this.THEME_KEY, theme);
        } catch (error) {
            console.warn('Failed to save theme to localStorage:', error);
        }
    }

    /**
     * Get saved theme from localStorage
     */
    private getSavedTheme(): Theme {
        try {
            const saved = localStorage.getItem(this.THEME_KEY);
            if (saved === 'dark' || saved === 'light') {
                return saved;
            }
        } catch (error) {
            console.warn('Failed to load theme from localStorage:', error);
        }

        // Default to light theme
        return 'light';
    }

    /**
     * Detect system theme preference
     */
    getSystemTheme(): Theme {
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    }

    /**
     * Use system theme preference
     */
    useSystemTheme(): void {
        const systemTheme = this.getSystemTheme();
        this.setTheme(systemTheme);
    }
}
